import { Prisma, type PrismaClient } from '@prisma/client';
import { normalisePhone } from '@roovers/shared';
import { z } from 'zod';

type Db = PrismaClient | Prisma.TransactionClient;

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

/** Phone numbers are stored as +256... A number that cannot be normalised is a validation error. */
const phone = z
  .string()
  .trim()
  .max(30)
  .nullable()
  .optional()
  .transform((v, ctx) => {
    if (!v) return null;
    const n = normalisePhone(v);
    if (!n) {
      ctx.addIssue({ code: 'custom', message: `"${v}" is not a valid phone number` });
      return z.NEVER;
    }
    return n;
  });

export const contactSchema = z.object({
  id: z.number().int().positive().optional(),
  name: z.string().trim().min(1, 'Contact name is required').max(200),
  position: text(100),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email('Invalid email address')
    .max(200)
    .or(z.literal(''))
    .transform((v) => v || null)
    .nullable()
    .optional(),
  phone,
  whatsappNumber: phone,
  receivesDocuments: z.boolean().default(false),
  isPrimary: z.boolean().default(false),
});
export type ContactInput = z.infer<typeof contactSchema>;

const money = z
  .union([z.string(), z.number()])
  .transform((v) => String(v).trim())
  .refine((v) => v === '' || /^\d{1,15}(\.\d{1,4})?$/.test(v), 'Enter a valid amount')
  .transform((v) => (v === '' ? null : v));

export const partyInputSchema = z
  .object({
    name: z.string().trim().min(2, 'Name must be at least 2 characters').max(200),
    type: z.enum(['company', 'individual']).default('company'),
    isClient: z.boolean().default(false),
    isProspect: z.boolean().default(false),
    isVendor: z.boolean().default(false),
    tin: text(50),
    vatRegistered: z.boolean().default(false),
    vatNumber: text(50),
    address: text(500),
    city: text(100),
    country: z.string().trim().min(2).max(100).default('Uganda'),
    website: text(200),
    industry: text(100),
    currency: z
      .string()
      .trim()
      .length(3)
      .transform((v) => v.toUpperCase())
      .default('UGX'),
    paymentTermsDays: z.number().int().min(0).max(365).default(30),
    creditLimit: money.nullable().optional(),
    whtAgent: z.boolean().default(false),
    whtApplicable: z.boolean().default(false),
    paymentDetails: text(2000),
    notes: text(4000),
    ownerId: z.number().int().positive().nullable().optional(),
    tags: z.array(z.string().trim().min(1).max(50)).max(20).default([]),
    contacts: z.array(contactSchema).max(30).default([]),
    customFields: z.record(z.union([z.string(), z.number(), z.boolean(), z.null()])).optional(),
  })
  .refine((v) => v.isClient || v.isProspect || v.isVendor, {
    message: 'Choose at least one role: client, prospect or vendor',
    path: ['isClient'],
  });
export type PartyInput = z.infer<typeof partyInputSchema>;

export const partyInclude = {
  contacts: true,
  tags: { include: { tag: true } },
} satisfies Prisma.PartyInclude;
export type PartyWithRelations = Prisma.PartyGetPayload<{ include: typeof partyInclude }>;

export function partyView(p: PartyWithRelations) {
  return {
    id: p.id,
    name: p.name,
    type: p.type,
    isClient: p.isClient,
    isProspect: p.isProspect,
    isVendor: p.isVendor,
    needsReview: p.needsReview,
    tin: p.tin,
    vatRegistered: p.vatRegistered,
    vatNumber: p.vatNumber,
    address: p.address,
    city: p.city,
    country: p.country,
    website: p.website,
    industry: p.industry,
    currency: p.currency,
    paymentTermsDays: p.paymentTermsDays,
    creditLimit: p.creditLimit ? p.creditLimit.toFixed(4) : null,
    whtAgent: p.whtAgent,
    whtApplicable: p.whtApplicable,
    paymentDetails: p.paymentDetails,
    notes: p.notes,
    ownerId: p.ownerId,
    customFields: p.customFields,
    active: p.active,
    tags: p.tags.map((t) => t.tag.name).sort(),
    contacts: p.contacts
      .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || a.id - b.id)
      .map((c) => ({
        id: c.id,
        name: c.name,
        position: c.position,
        email: c.email,
        phone: c.phone,
        whatsappNumber: c.whatsappNumber,
        receivesDocuments: c.receivesDocuments,
        isPrimary: c.isPrimary,
      })),
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
  };
}

async function tagIds(tx: Db, companyId: number, names: string[]): Promise<number[]> {
  const ids: number[] = [];
  for (const name of [...new Set(names.map((n) => n.trim()).filter(Boolean))]) {
    const tag = await tx.tag.upsert({
      where: { companyId_name: { companyId, name } },
      create: { companyId, name },
      update: {},
    });
    ids.push(tag.id);
  }
  return ids;
}

/** The first contact of a party is its primary one unless another is flagged. */
function normaliseContacts(contacts: ContactInput[]): ContactInput[] {
  if (contacts.length === 0) return contacts;
  const hasPrimary = contacts.some((c) => c.isPrimary);
  return contacts.map((c, i) => ({
    ...c,
    isPrimary: hasPrimary ? c.isPrimary : i === 0,
    receivesDocuments: c.receivesDocuments || (!hasPrimary && i === 0 && !!c.email),
  }));
}

export async function createParty(
  tx: Db,
  args: { companyId: number; userId: number; input: PartyInput; needsReview?: boolean },
) {
  const { input } = args;
  const ids = await tagIds(tx, args.companyId, input.tags);
  const created = await tx.party.create({
    data: {
      companyId: args.companyId,
      name: input.name,
      type: input.type,
      isClient: input.isClient,
      isProspect: input.isProspect,
      isVendor: input.isVendor,
      needsReview: args.needsReview ?? false,
      tin: input.tin,
      vatRegistered: input.vatRegistered,
      vatNumber: input.vatNumber,
      address: input.address,
      city: input.city,
      country: input.country,
      website: input.website,
      industry: input.industry,
      currency: input.currency,
      paymentTermsDays: input.paymentTermsDays,
      creditLimit: input.creditLimit ?? undefined,
      whtAgent: input.whtAgent,
      whtApplicable: input.whtApplicable,
      paymentDetails: input.paymentDetails,
      notes: input.notes,
      ownerId: input.ownerId ?? args.userId,
      customFields: input.customFields,
      createdBy: args.userId,
      contacts: {
        create: normaliseContacts(input.contacts).map(({ id: _id, ...c }) => c),
      },
      tags: { create: ids.map((tagId) => ({ tagId })) },
    },
    include: partyInclude,
  });
  return created;
}

export async function updateParty(
  tx: Db,
  args: {
    companyId: number;
    userId: number;
    id: number;
    input: Partial<PartyInput> & { contacts?: ContactInput[]; tags?: string[] };
  },
) {
  const { input, id } = args;
  const { contacts, tags, customFields, ...rest } = input;
  const data: Prisma.PartyUpdateInput = { updatedBy: args.userId };
  for (const [k, v] of Object.entries(rest)) {
    if (v !== undefined) (data as Record<string, unknown>)[k] = v;
  }
  if (customFields !== undefined) data.customFields = customFields;
  if (tags) {
    const ids = await tagIds(tx, args.companyId, tags);
    await tx.partyTag.deleteMany({ where: { partyId: id } });
    data.tags = { create: ids.map((tagId) => ({ tagId })) };
  }
  if (contacts) {
    await tx.partyContact.deleteMany({ where: { partyId: id } });
    data.contacts = { create: normaliseContacts(contacts).map(({ id: _id, ...c }) => c) };
  }
  return tx.party.update({ where: { id }, data, include: partyInclude });
}

export interface DuplicateHit {
  id: number;
  name: string;
  reason: string;
  isClient: boolean;
  isProspect: boolean;
  isVendor: boolean;
}

/** Suggests existing records that look like the one being created (name, TIN, email, phone). */
export async function findDuplicates(
  db: Db,
  companyId: number,
  probe: { name?: string; tin?: string; email?: string; phone?: string; excludeId?: number },
): Promise<DuplicateHit[]> {
  const hits = new Map<number, DuplicateHit>();
  const add = (
    p: { id: number; name: string; isClient: boolean; isProspect: boolean; isVendor: boolean },
    reason: string,
  ) => {
    if (probe.excludeId === p.id) return;
    const prev = hits.get(p.id);
    hits.set(p.id, {
      id: p.id,
      name: p.name,
      isClient: p.isClient,
      isProspect: p.isProspect,
      isVendor: p.isVendor,
      reason: prev ? `${prev.reason}, ${reason}` : reason,
    });
  };
  const base = { companyId, active: true };
  const pick = { id: true, name: true, isClient: true, isProspect: true, isVendor: true } as const;

  if (probe.tin?.trim()) {
    for (const p of await db.party.findMany({
      where: { ...base, tin: probe.tin.trim() },
      select: pick,
      take: 5,
    }))
      add(p, 'same TIN');
  }
  if (probe.email?.trim()) {
    const email = probe.email.trim().toLowerCase();
    for (const c of await db.partyContact.findMany({
      where: { email, party: base },
      include: { party: { select: pick } },
      take: 5,
    }))
      add(c.party, 'same email');
  }
  if (probe.phone?.trim()) {
    const n = normalisePhone(probe.phone);
    if (n) {
      for (const c of await db.partyContact.findMany({
        where: { OR: [{ phone: n }, { whatsappNumber: n }], party: base },
        include: { party: { select: pick } },
        take: 5,
      }))
        add(c.party, 'same phone');
    }
  }
  if (probe.name?.trim().length && probe.name.trim().length >= 3) {
    const name = probe.name.trim();
    const lower = name.toLowerCase();
    const tokens = [...new Set(lower.split(/[^a-z0-9]+/).filter((t) => t.length >= 3))];
    // Candidates share the whole text or one of its words; similarity is then judged in code so a
    // longer or shorter variant of an existing name ("Acme Uganda Ltd Extra") is still caught.
    const candidates = await db.party.findMany({
      where: {
        ...base,
        OR: [
          { name: { contains: name } },
          ...tokens.slice(0, 4).map((t) => ({ name: { contains: t } })),
        ],
      },
      select: pick,
      take: 40,
    });
    const words = (n: string) =>
      new Set(
        n
          .toLowerCase()
          .split(/[^a-z0-9]+/)
          .filter((t) => t.length >= 2),
      );
    const probeWords = words(name);
    for (const p of candidates) {
      const other = p.name.toLowerCase();
      const otherWords = words(p.name);
      const shared = [...probeWords].filter((w) => otherWords.has(w)).length;
      const overlap = shared / Math.max(1, Math.min(probeWords.size, otherWords.size));
      if (other === lower) add(p, 'same name');
      else if (other.includes(lower) || lower.includes(other) || overlap >= 0.75)
        add(p, 'similar name');
    }
  }
  return [...hits.values()];
}

/** Returns a Prisma filter that limits a user with "own data" scope to records they own. */
export const ownScope = (auth: { scope: 'own' | 'all'; userId: number }): Prisma.PartyWhereInput =>
  auth.scope === 'own' ? { ownerId: auth.userId } : {};
